import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../services/AxiosInstance";

// GET /api/house-master
export const fetchHouseMasterThunk = createAsyncThunk(
  "houseMaster/fetchAll",
  async (
    {
      page = 1,
      limit = 10,
      search = "",
      status = "",
      connection = "",
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get("/api/house-master", {
        params: {
          page,
          limit,
          search,
          status,
          connection,
        },
      });

      if (!res.data?.success) {
        return rejectWithValue(res.data?.message || "Failed");
      }

      return {
        data: res.data?.data || [],
        pagination: res.data?.pagination || {
          page,
          limit,
          total: 0,
          totalPages: 1,
        },
        stats: res.data?.stats || {
          totalHouses: 0,
          waterConnections: 0,
          slrmConnections: 0,
          activeHouses: 0,
          inactiveHouses: 0,
        },
      };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
          err.message ||
          "Server error"
      );
    }
  }
);

// POST /api/house-master
export const createHouseThunk = createAsyncThunk(
  "houseMaster/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/house-master", payload);

      if (!res.data?.success) {
        return rejectWithValue(res.data?.message || "Failed");
      }

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
          err.message ||
          "Server error"
      );
    }
  }
);

// PUT /api/house-master/:home_id
export const updateHouseThunk = createAsyncThunk(
  "houseMaster/update",
  async ({ home_id, ...payload }, { rejectWithValue }) => {
    try {
      const res = await api.put(
        `/api/house-master/${home_id}`,
        payload
      );

      if (!res.data?.success) {
        return rejectWithValue(res.data?.message || "Failed");
      }

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
          err.message ||
          "Server error"
      );
    }
  }
);

// DELETE /api/house-master/:home_id
export const deleteHouseThunk = createAsyncThunk(
  "houseMaster/delete",
  async ({ home_id }, { rejectWithValue }) => {
    try {
      const res = await api.delete(
        `/api/house-master/${home_id}`
      );

      if (!res.data?.success) {
        return rejectWithValue(res.data?.message || "Failed");
      }

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
          err.message ||
          "Server error"
      );
    }
  }
);

// BULK IMPORT
export const bulkImportHouseThunk = createAsyncThunk(
  "houseMaster/bulkImport",
  async ({ file, sheet_name }, { rejectWithValue }) => {
    try {
      const fd = new FormData();

      fd.append("file", file);

      if (sheet_name) {
        fd.append("sheet_name", sheet_name);
      }

      const res = await api.post(
        "/api/house-master/bulk-import-file",
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (!res.data?.success) {
        return rejectWithValue(
          res.data?.message || "Bulk import failed"
        );
      }

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
          err.message ||
          "Server error"
      );
    }
  }
);

const houseMasterSlice = createSlice({
  name: "houseMaster",

  initialState: {
    list: [],

    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 1,
    },

    stats: {
      totalHouses: 0,
      waterConnections: 0,
      slrmConnections: 0,
      activeHouses: 0,
      inactiveHouses: 0,
    },

    loading: false,
    saving: false,
    error: null,

    importing: false,
    importResult: null,
    importError: null,
  },

  reducers: {
    clearHouseMasterError: (state) => {
      state.error = null;
    },

    clearImportState: (state) => {
      state.importing = false;
      state.importResult = null;
      state.importError = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // ==========================
      // FETCH HOUSE MASTER
      // ==========================

      .addCase(fetchHouseMasterThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(
        fetchHouseMasterThunk.fulfilled,
        (state, action) => {
          state.loading = false;

          state.list =
            action.payload?.data || [];

          state.pagination = {
            ...state.pagination,
            ...(action.payload?.pagination || {}),
          };

          state.stats = {
            ...state.stats,
            ...(action.payload?.stats || {}),
          };
        }
      )

      .addCase(
        fetchHouseMasterThunk.rejected,
        (state, action) => {
          state.loading = false;
          state.error =
            action.payload || "Failed to load houses";
        }
      )

      // ==========================
      // BULK IMPORT
      // ==========================

      .addCase(
        bulkImportHouseThunk.pending,
        (state) => {
          state.importing = true;
          state.importError = null;
          state.importResult = null;
        }
      )

      .addCase(
        bulkImportHouseThunk.fulfilled,
        (state, action) => {
          state.importing = false;
          state.importResult =
            action.payload || null;
        }
      )

      .addCase(
        bulkImportHouseThunk.rejected,
        (state, action) => {
          state.importing = false;
          state.importError =
            action.payload || "Bulk import failed";
        }
      )

      // ==========================
      // CREATE / UPDATE / DELETE
      // ==========================

      .addMatcher(
        (action) =>
          action.type ===
            createHouseThunk.pending.type ||
          action.type ===
            updateHouseThunk.pending.type ||
          action.type ===
            deleteHouseThunk.pending.type,

        (state) => {
          state.saving = true;
          state.error = null;
        }
      )

      .addMatcher(
        (action) =>
          action.type ===
            createHouseThunk.fulfilled.type ||
          action.type ===
            updateHouseThunk.fulfilled.type ||
          action.type ===
            deleteHouseThunk.fulfilled.type,

        (state) => {
          state.saving = false;
        }
      )

      .addMatcher(
        (action) =>
          action.type ===
            createHouseThunk.rejected.type ||
          action.type ===
            updateHouseThunk.rejected.type ||
          action.type ===
            deleteHouseThunk.rejected.type,

        (state, action) => {
          state.saving = false;
          state.error =
            action.payload || "Operation failed";
        }
      );
  },
});

export const {
  clearHouseMasterError,
  clearImportState,
} = houseMasterSlice.actions;

export default houseMasterSlice.reducer;